import { Text } from '@/components/animated-type/text';
import { AnnotatedType } from '@/components/annotated-type';
import { StoryHeader, StoryTagline, StoryTheme, StoryTitle } from '@/components/microstory/header';
import { Outro } from '@/components/microstory/outro';
import { StoryQuestion, useStoryQuestion } from '@/components/microstory/question';
import { StoriesNavigation } from '@/components/microstory/stories-navigation';
import { Theme, Themes } from '@/components/microstory/theme';
import { SeoHead } from '@/components/seo-head';
import { ChildhoodStridesChapter1 } from '@/components/stories/childhood-strides/chapter-1';
import { ChildhoodStridesChapter2 } from '@/components/stories/childhood-strides/chapter-2';
import { SurveyBackground } from '@/components/survey-background';
import data from '@/data/childhood-strides.json';
import { createGetServerSideProps } from '@/utils/create-get-server-side-props';
import { getStoryProps } from '@/utils/get-story-props';
import { useLayout } from '@/utils/use-layout';
import { select, t, Trans } from '@lingui/macro';
import { InferGetServerSidePropsType } from 'next';
import { useSharingImage } from '@/utils/use-sharing-image';
import { useLingui } from '@lingui/react';

export const getServerSideProps = createGetServerSideProps(
  async () => ({ data: data as ChildhoodStridesData }),
  getStoryProps('childhood-strides', 'under-pressure')
);

export default function ChildhoodStrides(
  props: InferGetServerSidePropsType<typeof getServerSideProps>
) {
  const { storySlug, baseUrl, data, sharingUrl, linkedStoryIds, relatedStoryId } = props;

  useLayout('white');
  useLingui();

  const answers = [
    { id: 'fourOrMore', label: t({ id: 'childhood-strides.answer-1', message: 'Four or more' }) },
    {
      id: 'threeOrLess',
      label: t({ id: 'childhood-strides.answer-2', message: 'Three or fewer' }),
    },
  ];

  const sharingImage = useSharingImage('childhood-strides');
  const pageTitle = t({ id: 'childhood-strides.title', message: 'Childhood strides' });
  const pageDescription = t({
    id: 'childhood-strides.description',
    message:
      'Nostalgia can be a powerful force. But when it comes to childhood, has life changed for the better?',
  });

  const { selectedAnswer, handleAnswer } = useStoryQuestion();

  return (
    <>
      <SeoHead
        baseUrl={baseUrl}
        pageTitle={pageTitle}
        pageDescription={pageDescription}
        sharingImage={sharingImage}
      />

      <Theme theme={Themes.lime} hasBackButton>
        <StoryHeader backgroundId={storySlug}>
          <StoryTheme>
            <Trans id="childhood-strides.theme">Childhood Progress</Trans>
          </StoryTheme>
          <StoryTitle>{pageTitle}</StoryTitle>
          <StoryTagline>
            <Trans id="childhood-strides.tagline">
              <Text type="heading">
                Nostalgia can be a powerful force. But when it comes to childhood, has life changed
                for the <span>better?</span>
              </Text>
            </Trans>
          </StoryTagline>
        </StoryHeader>

        <SurveyBackground />

        <StoryQuestion
          question={t({
            message: `Consider the following seven dimensions of childhood: quality of health care, quality of education, physical safety, mental well-being, opportunities to play, access to healthy food, and access to clean water.`,
            id: 'childhood-strides.story-question.question',
          })}
          answerHeading={t({
            id: 'childhood-strides.question.answer-heading',
            message:
              "How many of these seven dimensions do you think are better for children today than for their parents' generation?",
          })}
          answers={answers}
          selectedAnswer={selectedAnswer}
          response={
            selectedAnswer?.id &&
            t({
              id: 'childhood-strides.response',
              message: select(selectedAnswer.id, {
                fourOrMore: 'Most people agree',
                threeOrLess: 'Most people feel differently',
              }),
            })
          }
          onAnswer={handleAnswer}
        />

        <ChildhoodStridesChapter1 data={data.strides} />
        <ChildhoodStridesChapter2 data={data.strides_country_generation_averages} />

        <Outro
          css={{
            marginBlockStart: '25vh',
            '@bp11': {
              marginBlockStart: 0,
            },
          }}
          sharing={{
            title: t({ id: 'childhood-strides.sharing.title', message: 'Childhood strides' }),
            text: t({
              id: 'childhood-strides.sharing.text',
              message:
                'Nostalgia can be a powerful force. But when it comes to childhood, has life changed for the better?',
            }),
            url: sharingUrl,
          }}
          relatedStoryId={relatedStoryId}
        >
          <Trans id="childhood-strides.closing-question">
            Will progress in different dimensions of childhood{' '}
            <AnnotatedType>continue to improve</AnnotatedType> over the next generation?
          </Trans>
        </Outro>
      </Theme>

      <StoriesNavigation storyIds={linkedStoryIds} />
    </>
  );
}
